import L from "leaflet";

import type { OrderStatus } from "@/types/orders";
import type { RouteStop } from "@/types/routes";
import { escapeHtml } from "@/lib/escapeHtml";

/**
 * Unified map-marker icon system for the route-planning maps.
 *
 * This module is the single source of truth for every DivIcon rendered on the
 * overview map (`RouteMapPanel.tsx`, `/routes`) and the detail map
 * (`RouteDetailPage.tsx`, `/routes/:id`). Both maps consume the same factories
 * so the same underlying entity renders identically (size / border / shadow /
 * number style / contrast); only the injected fill color differs by intent —
 * the overview passes the route color, the detail map passes the status color.
 *
 * DivIcon HTML is the documented inline-style exception (Leaflet requires an
 * inline HTML/style string). No other inline styles are used in the app.
 *
 * ── Size + color + contrast scale (single documented scale) ──────────────────
 *  - Delivery stop pin: 28px normal / 34px selected (round). Number 12px / 700.
 *  - Depot chip: 26px rounded-square. Warehouse chip: 26px rounded-square.
 *    Depot + warehouse form ONE rounded-square chip family, visually distinct
 *    from the round delivery-stop pins.
 *  - Unassigned pin: 20px round.
 *  - Shared border: 2px solid #fff. Shared shadow: STOP_SHADOW.
 *  - Selected stop adds a ring in the pin's own color.
 *  - Direction chevron: stroke width 2.2, stroke-linejoin: round.
 */

/** Font stack reused across every DivIcon so text metrics stay identical. */
const FONT_FAMILY = "system-ui,sans-serif";

/** Single shared shadow token for every pin/chip so depth reads consistently. */
const STOP_SHADOW = "0 2px 8px rgba(0,0,0,0.4)";

/** Shared white border on every pin/chip. */
const PIN_BORDER = "2px solid #fff";

/** Delivery-stop pin diameters (px). */
const STOP_SIZE = 28;
const STOP_SIZE_SELECTED = 34;

/** Depot + warehouse chip side length (px). */
const CHIP_SIZE = 26;

/** Unassigned pin diameter (px). */
const UNASSIGNED_SIZE = 22;

/**
 * The «no route» amber, and the white disc the dashed ring is drawn on.
 *
 * This is `brand-warning` from `tailwind.config.js`, resolved to its literal
 * hex ON PURPOSE. DivIcons take raw HTML STRINGS handed to Leaflet, so Tailwind
 * classes and `hsl(var(--…))` custom properties do not apply inside them —
 * unlike a react-leaflet `<Popup>`, whose React children DO get global
 * utilities (see `MapStopCard`).
 *
 * Using `brand-warning` and not `brand-primary` is deliberate and DEV_RULES
 * §10.10-compliant: "unassigned" is a MEANING (this order belongs to no
 * route), not a selected state or a brand accent, so it takes the FIXED
 * semantic token — which must read identically for every dealer — rather than
 * the per-dealer runtime brand colour. A dealer whose palette happened to be
 * amber would otherwise make «no route» invisible against their own chrome.
 *
 * Keep this value in step with `brand-warning` in `tailwind.config.js`.
 */
const UNASSIGNED_COLOR = "#FDAB3D";
const UNASSIGNED_FILL = "#fff";

/**
 * Route-color fallback palette used when `route.color` is null/undefined.
 *
 * AA contrast note (WCAG 2.1, white #fff text on the fill):
 * the amber and lime entries were DARKENED so the white stop number clears the
 * 4.5:1 AA threshold (product decision: darken, keep all 8 distinct colors, no
 * conditional dark/light text). Measured white-on-fill contrast ratios:
 *   - amber  #d97706 → 3.19:1 (FAIL)  darkened to #b45309 → 5.02:1 (PASS)
 *   - lime   #65a30d → 3.09:1 (FAIL)  darkened to #4d7c0f → 4.99:1 (PASS)
 * The other six entries already clear AA for white text.
 */
export const ROUTE_COLORS = [
  "#2563eb", // blue
  "#dc2626", // red
  "#16a34a", // green
  "#b45309", // amber-700 (darkened from #d97706; white contrast 5.02:1)
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#be185d", // pink
  "#4d7c0f", // lime-700 (darkened from #65a30d; white contrast 4.99:1)
];

/** Chevron stroke width for direction arrows (shared with the legend swatch). */
export const CHEVRON_STROKE_WIDTH = 2.2;

/** Returns true for depot stops (start, mid, end); false for regular order stops. */
export function isDepotStop(stop: RouteStop): boolean {
  return stop.stop_type !== "order";
}

/**
 * Creates a Leaflet DivIcon for a depot stop marker.
 * Color scheme: start_depot = green, mid_depot = orange, end_depot = red.
 * Icon is a 26×26 rounded square with a letter label and a colored border —
 * the reference chip design that the warehouse chip matches.
 */
export function createDepotIcon(
  type: "start_depot" | "mid_depot" | "end_depot",
): L.DivIcon {
  const colors: Record<string, string> = {
    start_depot: "#16a34a",
    mid_depot: "#ea580c",
    end_depot: "#dc2626",
  };
  const labels: Record<string, string> = {
    start_depot: "S",
    mid_depot: "M",
    end_depot: "E",
  };
  const color = colors[type] ?? "#374151";
  const label = labels[type] ?? "W";

  return L.divIcon({
    html: `<div style="width:${CHIP_SIZE}px;height:${CHIP_SIZE}px;background:#fff;border:3px solid ${color};border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;color:${color};font-family:${FONT_FAMILY};box-shadow:${STOP_SHADOW}">${label}</div>`,
    className: "",
    iconSize: [CHIP_SIZE, CHIP_SIZE],
    iconAnchor: [CHIP_SIZE / 2, CHIP_SIZE / 2],
  });
}

/**
 * Fill of the GPS route-origin pin (BUG-442). Teal sits outside the depot
 * palette (green / orange / red) and outside the neutral warehouse chip, so the
 * vehicle origin can never be mistaken for a depot at a glance.
 */
const ROUTE_START_COLOR = "#0d9488"; // teal-600

/** GPS route-origin pin box (px) — a teardrop, so width ≠ height. */
const ROUTE_START_WIDTH = 24;
const ROUTE_START_HEIGHT = 32;

/**
 * GPS route-origin marker (BUG-442) — the point the route was actually planned
 * from when the tenant's "consider object locations" setting anchored the solver
 * on the vehicle's live position instead of the start warehouse. Rendered only
 * for routes whose `start_lat`/`start_lon` are non-null; warehouse-anchored
 * routes (the common case) show no such marker at all.
 *
 * Deliberately a THIRD marker shape, so it reads as neither of the two existing
 * families even in grayscale:
 *  - depot / warehouse = 26px rounded-square chip (letter or warehouse glyph),
 *  - delivery stop = round numbered pin,
 *  - route origin = teardrop pin with a navigation-arrow glyph, teal fill.
 *
 * Anchor note (BUG-265..268): the drawn shape is a teardrop whose TIP is the
 * true coordinate, so `iconAnchor` is the bottom-center of the box
 * (`[w / 2, h]`), not its center — anchoring at the center would float the pin
 * half its height above the position it marks.
 */
export function createRouteStartIcon(): L.DivIcon {
  const w = ROUTE_START_WIDTH;
  const h = ROUTE_START_HEIGHT;
  // lucide-react `Navigation` glyph (24×24 viewBox), scaled to 12px and
  // translated so it sits centered inside the 24px-wide pin head (center y=11).
  const glyph = `<g transform="translate(6 5) scale(0.5)" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"><polygon points="3 11 22 2 13 21 11 13 3 11"/></g>`;
  const html = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 24 32" style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35))"><path d="M12 1a10 10 0 0 0-10 10c0 6.9 8.7 19 9.1 19.5a1.1 1.1 0 0 0 1.8 0C13.3 30 22 17.9 22 11A10 10 0 0 0 12 1Z" fill="${ROUTE_START_COLOR}" stroke="#fff" stroke-width="2"/>${glyph}</svg>`;
  return L.divIcon({
    html,
    className: "",
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h],
  });
}

interface StopIconOptions {
  /** 1-based stop position number shown inside the pin. */
  position: number;
  /** Fill color — route color on the overview, status color on the detail map. */
  color: string;
  /** Renders the enlarged (34px) selected/highlighted variant with a ring. */
  selected?: boolean;
}

/**
 * Builds the inner HTML for a round numbered stop pin. Shared by the plain and
 * `+N` stacked-badge variants so they render on the exact same size/border/
 * shadow/number scale, differing only in the injected fill color.
 */
function stopPinHtml(label: string, color: string, selected: boolean): string {
  const size = selected ? STOP_SIZE_SELECTED : STOP_SIZE;
  const ring = selected
    ? `box-shadow:0 0 0 3px ${color},${STOP_SHADOW};`
    : `box-shadow:${STOP_SHADOW};`;
  return `<div style="width:${size}px;height:${size}px;background:${color};border:${PIN_BORDER};border-radius:50%;${ring}display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:12px;font-family:${FONT_FAMILY}">${label}</div>`;
}

/**
 * Numbered delivery-stop pin. This one factory backs BOTH the overview
 * (route-color fill) and the detail map (status-color fill) so the same stop is
 * identical in size/border/shadow/number-style/contrast on both maps. Pass
 * `selected` for the enlarged 34px ring variant (the overview's highlight and
 * the detail map's hover both map onto this state).
 */
export function createStopIcon({
  position,
  color,
  selected = false,
}: StopIconOptions): L.DivIcon {
  const size = selected ? STOP_SIZE_SELECTED : STOP_SIZE;
  return L.divIcon({
    html: stopPinHtml(String(position), color, selected),
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

interface StopBadgeIconOptions {
  /** Lowest stop position number in the coincident group (shown in the pin). */
  position: number;
  /** How many additional stops are stacked at this coordinate (the "+N"). */
  extraCount: number;
  /** Fill color of the base pin. */
  color: string;
  /** Enlarged 34px selected variant. */
  selected?: boolean;
}

/**
 * `+N` stacked-badge stop pin — a numbered stop pin (showing the lowest
 * position number) with a small "+N" badge in the top-right corner to signal
 * that N additional stops share this coordinate. Consumed by TASK-4341's
 * coincident-stop grouping.
 */
export function createStopBadgeIcon({
  position,
  extraCount,
  color,
  selected = false,
}: StopBadgeIconOptions): L.DivIcon {
  const size = selected ? STOP_SIZE_SELECTED : STOP_SIZE;
  const badge = `<div style="position:absolute;top:-6px;right:-8px;min-width:16px;height:16px;padding:0 3px;background:#111827;border:${PIN_BORDER};border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:10px;font-family:${FONT_FAMILY};box-sizing:border-box">+${extraCount}</div>`;
  return L.divIcon({
    html: `<div style="position:relative;width:${size}px;height:${size}px">${stopPinHtml(String(position), color, selected)}${badge}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/** Neutral chip background for the warehouse glyph (matches depot chip family). */
const WAREHOUSE_CHIP_COLOR = "#374151";

/**
 * Warehouse marker — ONE rounded-square chip (same chip family as the depot
 * chip) containing the lucide `Warehouse` glyph. Replaces both the crude
 * div-"house" (RouteMapPanel) and the bare "W" circle (RouteDetailPage).
 * The lucide SVG path data is inlined as static markup (never a React
 * component mounted inside a DivIcon).
 */
export function createWarehouseIcon(): L.DivIcon {
  // lucide-react `Warehouse` icon paths (v0.468.0), rendered white at 14px.
  const glyph = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/><rect width="12" height="12" x="6" y="10"/></svg>`;
  return L.divIcon({
    html: `<div style="width:${CHIP_SIZE}px;height:${CHIP_SIZE}px;background:${WAREHOUSE_CHIP_COLOR};border:${PIN_BORDER};border-radius:6px;display:flex;align-items:center;justify-content:center;box-shadow:${STOP_SHADOW}">${glyph}</div>`,
    className: "",
    iconSize: [CHIP_SIZE, CHIP_SIZE],
    iconAnchor: [CHIP_SIZE / 2, CHIP_SIZE / 2],
  });
}

interface UnassignedIconOptions {
  /** Blue check when selected for batch-add; gray plus otherwise. */
  selected?: boolean;
}

/**
 * Unassigned-order pin — a DASHED amber ring on white with a «+» (mockup 2).
 *
 * ## Why it is dashed rather than another solid circle
 *
 * It used to be a solid grey disc, which is exactly what a numbered route stop
 * is: a solid coloured disc. On a map holding both, the only difference was
 * hue — and grey reads as "disabled/inactive", not as "needs a decision". The
 * dispatcher's core task on this page is spotting orders that are on NO route,
 * so those pins must differ by SHAPE, which survives colour-blindness, a dealer
 * palette change and a greyscale print.
 *
 * The dashed outline carries "a placeholder, not yet placed" and matches the
 * dashed «+» badge on the unassigned card, so the pin and the card read as the
 * same object. Selected fills solid with the check glyph, so an order chosen
 * for batch-add reads as committed.
 *
 * BUG-291: both glyphs are fixed-size symmetric SVGs, because text "+"/"✓"
 * glyph metrics do not line up with the flexbox center. The box stays a real
 * square (never iconSize [0,0] — the documented oval pitfall) with
 * `box-sizing:border-box`.
 */
export function createUnassignedIcon({
  selected = false,
}: UnassignedIconOptions = {}): L.DivIcon {
  const glyph = selected
    ? `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`
    : `<svg width="10" height="10" viewBox="0 0 12 12"><path d="M6 2v8M2 6h8" stroke="${UNASSIGNED_COLOR}" stroke-width="2.4" stroke-linecap="round"/></svg>`;
  // Selected: solid fill, white glyph, white border + amber halo — the same
  // "chosen" language every other pin uses. Unselected: white disc, dashed
  // amber ring, amber glyph.
  const box = selected
    ? `background:${UNASSIGNED_COLOR};border:${PIN_BORDER};box-shadow:0 0 0 3px ${UNASSIGNED_COLOR}55,${STOP_SHADOW};`
    : `background:${UNASSIGNED_FILL};border:2px dashed ${UNASSIGNED_COLOR};box-shadow:${STOP_SHADOW};`;
  return L.divIcon({
    html: `<div style="width:${UNASSIGNED_SIZE}px;height:${UNASSIGNED_SIZE}px;box-sizing:border-box;${box}border-radius:50%;display:flex;align-items:center;justify-content:center">${glyph}</div>`,
    className: "",
    iconSize: [UNASSIGNED_SIZE, UNASSIGNED_SIZE],
    iconAnchor: [UNASSIGNED_SIZE / 2, UNASSIGNED_SIZE / 2],
  });
}

/** Order-pin diameters (px) on the Orders map. */
const ORDER_PIN_SIZE = 18;
const ORDER_PIN_SIZE_SELECTED = 22;

/**
 * Order-status → pin fill color for the Orders map.
 *
 * Sourced from the existing `STATUS_STYLE` palette in `OrdersPage.tsx` (the
 * `dot` Tailwind classes), resolved to the exact hex values so the map pins,
 * the list dots and the legend swatches all read the same color. Keyed to the
 * 7 real backend `Order.Status` values (kept consistent with the S159
 * status realignment). Do NOT fork this palette — it must track `STATUS_STYLE`.
 */
export const ORDER_STATUS_PIN_COLORS: Record<OrderStatus, string> = {
  new: "#60a5fa", // bg-blue-400
  in_route: "#fb923c", // bg-orange-400
  visited: "#38bdf8", // bg-sky-400
  confirmed: "#10b981", // bg-emerald-500
  rejected: "#f43f5e", // bg-rose-500
  skipped: "#fbbf24", // bg-amber-400
  archived: "#9ca3af", // bg-gray-400
};

/** Fallback pin color for an unknown status (defaults to the `new` blue). */
const ORDER_STATUS_PIN_FALLBACK = ORDER_STATUS_PIN_COLORS.new;

/**
 * Plain order pin for the Orders map (`OrdersPage.tsx`, `/orders`).
 *
 * Unlike the numbered delivery-stop pin (`createStopIcon`), an order on the
 * Orders list map is a small solid dot with no number. The fill encodes the
 * order STATUS (via `ORDER_STATUS_PIN_COLORS`) so the map conveys status at a
 * glance, matching the list status dots. Selection is shown WITHOUT losing the
 * status color: a selected pin keeps its status fill and gains a white +
 * dark-slate double ring and a slightly larger size. Every pin keeps a 2px
 * white border in all states (satellite-contrast halo). Consolidated here from
 * the former inline `createOrderMarkerIcon` in `OrdersPage.tsx` so both maps
 * source their markers from this module.
 */
export function createOrderPinIcon(
  status: OrderStatus,
  selected: boolean,
): L.DivIcon {
  const fill = ORDER_STATUS_PIN_COLORS[status] ?? ORDER_STATUS_PIN_FALLBACK;
  const size = selected ? ORDER_PIN_SIZE_SELECTED : ORDER_PIN_SIZE;
  const shadow = selected
    ? "box-shadow:0 0 0 2.5px #ffffff,0 0 0 4px #0f172a,0 2px 8px rgba(15,23,42,0.45)"
    : "box-shadow:0 2px 6px rgba(15,23,42,0.35)";
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:${fill};border:2px solid #ffffff;border-radius:50%;${shadow}"></div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Permanent-order (template) pin fill on the Orders map.
 *
 * Violet, deliberately OUTSIDE the seven order-status colors in
 * `ORDER_STATUS_PIN_COLORS` — a permanent order has no status at all, so it must
 * not borrow a status hue. Matches the violet `PermanentOrderBadge` used for the
 * same rows in the table, so the two surfaces read as the same thing.
 */
const PERMANENT_PIN_COLOR = "#7c3aed"; // violet-600 (badge family)

/** Permanent-order pin side length (px) — same scale as the order dot. */
const PERMANENT_PIN_SIZE = 18;
const PERMANENT_PIN_SIZE_SELECTED = 22;

/**
 * Permanent-order (template) pin for the Orders map (`OrdersPage.tsx`).
 *
 * TASK-4707. Distinct from `createOrderPinIcon` in BOTH channels a dispatcher
 * can read at a glance:
 *  - SHAPE: a rounded SQUARE chip, not a round dot. This reuses the established
 *    "chip family vs round pin" split already used for depot/warehouse chips, so
 *    the two kinds stay distinguishable in grayscale / for color-blind users.
 *  - COLOR: violet (`#7c3aed`), which is not one of the seven status colors — a
 *    permanent order has no status, so it must not borrow a status hue.
 * The `Repeat` glyph inside mirrors the table badge's icon.
 *
 * Anchor note (BUG-265..268): `iconSize` is a REAL square and `iconAnchor` is its
 * exact center, so the chip sits ON its coordinate rather than offset from it.
 * `box-sizing:border-box` keeps the 2px white border inside that box.
 */
export function createPermanentOrderPinIcon(selected: boolean): L.DivIcon {
  const size = selected ? PERMANENT_PIN_SIZE_SELECTED : PERMANENT_PIN_SIZE;
  const shadow = selected
    ? "box-shadow:0 0 0 2.5px #ffffff,0 0 0 4px #0f172a,0 2px 8px rgba(15,23,42,0.45)"
    : "box-shadow:0 2px 6px rgba(15,23,42,0.35)";
  // lucide-react `Repeat` icon paths, drawn white at 10px, geometrically
  // centered by the flexbox (same approach as the unassigned pin, BUG-291).
  const glyph = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>`;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;box-sizing:border-box;background:${PERMANENT_PIN_COLOR};border:2px solid #ffffff;border-radius:5px;${shadow};display:flex;align-items:center;justify-content:center">${glyph}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Blue count-circle for clusters of order markers on the Orders map. The blue
 * fill (and blue-tinted shadow) distinguishes it from the gray unassigned-order
 * cluster used on the route-planning maps. Consolidated here from the former
 * inline `createClusterIcon` in `OrdersPage.tsx`.
 */
export function createOrderClusterIcon(cluster: {
  getChildCount(): number;
}): L.DivIcon {
  const count = cluster.getChildCount();
  const size = count < 10 ? 36 : count < 50 ? 44 : 52;
  const fontSize = count < 10 ? 14 : 12;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:#2563eb;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(37,99,235,0.5);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:${fontSize}px;font-family:${FONT_FAMILY}">${count}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Cluster badge for unassigned orders — a DASHED amber pill «+ 3 заказа»
 * (mockup 2, top-right).
 *
 * Shares the dashed-amber language of {@link createUnassignedIcon} so a cluster
 * of unplaced orders is recognisably the same KIND of thing as a single one —
 * previously it was a solid grey circle, visually identical in shape to the
 * blue order cluster and to a route stop, differing only in hue.
 *
 * ## Why the label is injected rather than built here
 *
 * The pill reads «3 заказа», which is a pluralised, translated noun. This is a
 * plain function called by Leaflet's `iconCreateFunction` — it is outside React
 * and has no `t`. Rather than hardcode Russian (or, worse, build a plural by
 * string-concatenating a suffix, which is wrong in both locales and in every
 * Slavic plural form), the caller passes an already-translated `label` built
 * with `t(..., { count })`, so i18next's plural rules apply normally.
 *
 * `label` is optional: with no label the pill shows the bare count, which keeps
 * the function usable (and its existing callers correct) without a translator.
 */
export function createUnassignedClusterIcon(
  cluster: { getChildCount(): number },
  label?: string,
): L.DivIcon {
  const count = cluster.getChildCount();
  const text = label ?? String(count);
  const fontSize = 12;
  // A pill's width depends on its text, which cannot be measured here. Leaflet
  // needs a box, so estimate from the character count and let the content size
  // itself: `width:max-content` keeps the visual pill exact even when the
  // estimate is off, while iconSize/iconAnchor stay a sane hit box.
  const width = Math.max(56, 22 + text.length * 7);
  const height = 26;
  return L.divIcon({
    html: `<div style="width:max-content;height:${height}px;box-sizing:border-box;background:${UNASSIGNED_FILL};border:2px dashed ${UNASSIGNED_COLOR};border-radius:${height / 2}px;box-shadow:${STOP_SHADOW};display:flex;align-items:center;gap:5px;padding:0 10px;color:${UNASSIGNED_COLOR};font-weight:700;font-size:${fontSize}px;font-family:${FONT_FAMILY};white-space:nowrap"><svg width="9" height="9" viewBox="0 0 12 12"><path d="M6 2v8M2 6h8" stroke="${UNASSIGNED_COLOR}" stroke-width="2.4" stroke-linecap="round"/></svg>${escapeHtml(text)}</div>`,
    className: "",
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
  });
}

interface DirectionArrowIconOptions {
  /** Rotation in degrees applied to the chevron (travel bearing). */
  bearing: number;
  /** Chevron stroke color (route color). */
  color: string;
  /** Chevron box side length in px (default 12). */
  size?: number;
}

/**
 * Direction chevron placed along a route polyline to show travel direction.
 * Non-interactive. Stroke width 2.2 with `stroke-linejoin: round` (unified).
 * TASK-4343 gates WHERE these are shown; this factory only builds the icon.
 */
export function createDirectionArrowIcon({
  bearing,
  color,
  size = 12,
}: DirectionArrowIconOptions): L.DivIcon {
  return L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 12 12" style="transform:rotate(${bearing}deg);display:block"><polyline points="3,9 6,3 9,9" fill="none" stroke="${color}" stroke-width="${CHEVRON_STROKE_WIDTH}" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Branded point-picker pin — a teardrop pin in the app's brand blue used by the
 * modal location pickers (create/edit order, order templates, create/edit
 * warehouse). Replaces the stock default Leaflet blue marker so those maps match
 * the rest of the app's custom markers (BUG-267). The tip sits at the bottom
 * center, so `iconAnchor` points there (the picked coordinate is the tip).
 */
export function createBrandedPinIcon(): L.DivIcon {
  const w = 26;
  const h = 34;
  // Round head + pointed tip; white border + shared shadow to match the family.
  const html = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 26 34" style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35))"><path d="M13 1C6.4 1 1 6.4 1 13c0 8.2 10.3 18.6 11.1 19.4a1.3 1.3 0 0 0 1.8 0C14.7 31.6 25 21.2 25 13 25 6.4 19.6 1 13 1Z" fill="#2563eb" stroke="#fff" stroke-width="2"/><circle cx="13" cy="13" r="4.5" fill="#fff"/></svg>`;
  return L.divIcon({
    html,
    className: "",
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h],
  });
}
